import React from 'react';
import Avatar from '@material-ui/core/Avatar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Box from '@material-ui/core/Box';
import LockOutlined from '@material-ui/icons/LockOutlined';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { Paper } from '@material-ui/core';
import { withApollo } from '@apollo/client/react/hoc';
import { useApolloClient } from '@apollo/client';
import background from '../../fakeData/Images/loginBg.jpg';
import SnackbarContext from '../../containers/CustomSnackbar/SnackbarContext';

function MadeWithLove() {
  return <div />;
}

const useStyles = makeStyles((theme) => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 16,
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  background: {
    position: 'absolute',
    width: '100%',
    height: '100vh',
  },
}));

const PrivacyPolicy = (props) => {
  const classes = useStyles();
  const client = useApolloClient();
  const { openSnackbar } = React.useContext(SnackbarContext);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: `url("${background}")`,
      backgroundRepeat: 'none',
      backgroundSize: '100%',
      display: 'flex',
      alignItems: 'center',
      justify: 'center',
    }}>
      <Container component="main" maxWidth="lg">
        <CssBaseline />
        <Paper className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockOutlined />
          </Avatar>

          <h1>Privacy Policy</h1>
          <p>Last updated: October 19, 2025</p>

          <p>Fitmate inc. ("we", "us", or "our") operates the Shalibo Wellness mobile application (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.</p>

          <p>Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy.</p>

          <h1>Information We Collect</h1>

          <h2>Personal Information</h2>
          <p>We collect the following types of personal information when you use our Service:</p>
          <ul>
            <li><strong>Account Information:</strong> Email address, username, password, and profile information</li>
            <li><strong>Contact Information:</strong> Name and email address for communication purposes</li>
          </ul>

          <h2>Health and Fitness Data</h2>
          <p><strong>IMPORTANT:</strong> Our app collects and processes health and fitness data to provide you with wellness tracking features. We collect the following health-related information:</p>
          <ul>
            <li><strong>Physical Activity Data:</strong> Step count, distance walked/run, calories burned, activity duration, and exercise sessions</li>
            <li><strong>Movement Data:</strong> Daily step counts, average step counts, walking and running distances</li>
            <li><strong>Heart Rate Data:</strong> Heart rate measurements and heart rate zones (if available from your device)</li>
            <li><strong>Sleep Data:</strong> Sleep duration and sleep patterns (if available and permitted)</li>
            <li><strong>Workout Data:</strong> Exercise types, workout duration, and intensity levels</li>
            <li><strong>Body Measurements:</strong> Weight, height, and BMI (if you choose to provide this information)</li>
            <li><strong>Fitness Goals:</strong> Your personal fitness targets and progress toward those goals</li>
            <li><strong>Activity History:</strong> Historical records of your physical activities and achievements</li>
          </ul>

          <h3>How We Access Health Data</h3>
          <p>We request your explicit permission to access health and fitness data from:</p>
          <ul>
            <li><strong>Google Fit:</strong> On Android devices, we request access to your Google Fit data to import step counts and physical activity information</li>
            <li><strong>Health Connect:</strong> On Android devices running Android 14 or higher, we request access to Health Connect (Android's unified health platform) to import step counts, physical activity data, and fitness metrics</li>
            <li><strong>Apple HealthKit:</strong> On iOS devices, we request access to your Apple Health data to import step counts and physical activity information</li>
          </ul>

          <p><strong>You have complete control:</strong> You can grant or revoke access to your health data at any time through your device settings. Revoking access will limit the functionality of step tracking features but will not delete your account.</p>

          <h3>How We Use Health Data</h3>
          <p>We use your health and fitness data solely for the following purposes:</p>
          <ul>
            <li>Displaying your daily and historical step counts within the app</li>
            <li>Calculating your average physical activity levels</li>
            <li>Tracking your progress toward fitness goals</li>
            <li>Monitoring your heart rate trends and workout intensity (if available)</li>
            <li>Analyzing your sleep patterns to provide wellness insights (if permitted)</li>
            <li>Recording and displaying your workout history and achievements</li>
            <li>Providing personalized wellness recommendations based on your activity</li>
            <li>Generating activity statistics and summaries for your personal use</li>
            <li>Calculating calories burned and energy expenditure estimates</li>
          </ul>

          <p><strong>We do NOT:</strong></p>
          <ul>
            <li>Sell your health data to third parties</li>
            <li>Use your health data for advertising purposes</li>
            <li>Share your health data with insurance companies</li>
            <li>Share identifiable health data with other users</li>
          </ul>

          <h2>Technical and Usage Data</h2>
          <p>We automatically collect certain information when you use our Service:</p>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
            <li><strong>Usage Information:</strong> App features used, session duration, interaction patterns</li>
            <li><strong>Analytics Data:</strong> Through Google Analytics, we collect information about how you use our Service, including pages visited and actions taken</li>
          </ul>

          <h1>How We Use Your Information</h1>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>To provide, maintain, and improve our Service</li>
            <li>To process and complete your fitness tracking activities</li>
            <li>To personalize your experience and provide tailored wellness recommendations</li>
            <li>To communicate with you about your account and Service updates</li>
            <li>To analyze usage patterns and improve app functionality</li>
            <li>To detect, prevent, and address technical issues and security threats</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h1>How We Share Your Information</h1>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following limited circumstances:</p>

          <h2>Third-Party Service Providers</h2>
          <p>We use trusted third-party services to help us operate our Service:</p>
          <ul>
            <li><strong>Google Analytics for Firebase:</strong> For app analytics and performance monitoring. Google Analytics collects usage data such as app interactions, crash reports, and device information. This data is anonymized and used only for improving our Service.</li>
            <li><strong>Google Play Services:</strong> For app functionality on Android devices</li>
          </ul>
          <p>These service providers have access to your information only to perform specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.</p>

          <h2>Legal Requirements</h2>
          <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</p>

          <h2>Business Transfers</h2>
          <p>If we are involved in a merger, acquisition, or asset sale, your information may be transferred. We will provide notice before your information is transferred and becomes subject to a different Privacy Policy.</p>

          <h1>Data Retention</h1>
          <p>We retain your information for as long as necessary to provide you with our Service and fulfill the purposes described in this Privacy Policy:</p>
          <ul>
            <li><strong>Account Information:</strong> Retained for the duration of your active account plus 90 days after account deletion</li>
            <li><strong>Health and Fitness Data:</strong> Retained for the duration of your active account plus 30 days after account deletion to allow for account recovery</li>
            <li><strong>Analytics Data:</strong> Anonymized analytics data may be retained for up to 26 months for statistical analysis</li>
            <li><strong>Communication Records:</strong> Support and communication records are retained for 2 years for quality assurance and legal compliance</li>
          </ul>

          <p>After these retention periods, your data will be permanently deleted from our systems and cannot be recovered.</p>

          <h1>Your Data Deletion Rights</h1>
          <p>You have the right to request deletion of your personal data at any time. Here's how:</p>

          <h2>How to Request Data Deletion</h2>
          <ol>
            <li><strong>In-App Deletion:</strong> Go to Settings → Account Settings → Delete Account. This will immediately deactivate your account and schedule your data for deletion.</li>
            <li><strong>Email Request:</strong> Send an email to office@shalibowellness.com with the subject line "Data Deletion Request" and include your registered email address.</li>
          </ol>

          <h2>Data Deletion Timeline</h2>
          <ul>
            <li><strong>Immediate:</strong> Your account is deactivated immediately upon request</li>
            <li><strong>Within 30 days:</strong> All your health and fitness data is permanently deleted from our active systems</li>
            <li><strong>Within 90 days:</strong> All your personal information is permanently deleted from our systems, including backups</li>
          </ul>

          <h2>What Gets Deleted</h2>
          <p>When you request data deletion, we will permanently delete:</p>
          <ul>
            <li>Your account information (email, username, profile)</li>
            <li>All health and fitness data (step counts, activity history, goals)</li>
            <li>Your preferences and settings</li>
            <li>Any content you've created within the app</li>
          </ul>

          <p><strong>Note:</strong> Anonymized analytics data that cannot be linked back to you may be retained for statistical purposes. We are legally required to retain certain information (such as transaction records) for tax and accounting purposes for up to 7 years.</p>

          <h2>Confirmation</h2>
          <p>Once your data deletion is complete, we will send you a confirmation email to notify you that your data has been permanently deleted from our systems.</p>

          <h1>Data Security</h1>
          <p>We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
          <ul>
            <li>Encryption of data in transit using SSL/TLS protocols</li>
            <li>Encryption of sensitive data at rest</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Regular data backups to prevent data loss</li>
          </ul>

          <p>However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee its absolute security.</p>

          <h1>Your Privacy Rights</h1>
          <p>Depending on your location, you may have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information (as described above)</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where we rely on consent</li>
            <li><strong>Object:</strong> Object to certain types of data processing</li>
          </ul>

          <p>To exercise these rights, please contact us at office@shalibowellness.com.</p>

          <h1>Children's Privacy</h1>
          <p>Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will delete such information from our systems.</p>

          <h1>International Data Transfers</h1>
          <p>Your information may be transferred to and maintained on servers located outside of your country of residence. By using our Service, you consent to the transfer of your information to countries that may have different data protection laws than your country.</p>

          <h1>Changes to This Privacy Policy</h1>
          <p>We may update our Privacy Policy from time to time. We will notify you of any significant changes by:</p>
          <ul>
            <li>Posting the new Privacy Policy on this page with an updated "Last updated" date</li>
            <li>Sending you an email notification (for material changes)</li>
            <li>Displaying an in-app notification when you next open the app</li>
          </ul>

          <p>You are advised to review this Privacy Policy periodically for any changes. Your continued use of the Service after changes are posted constitutes your acceptance of the updated Privacy Policy.</p>

          <h1>Third-Party Links and Services</h1>
          <p>Our Service may contain links to third-party websites or services that are not owned or controlled by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policies of any third-party sites you visit.</p>

          <h2>Third-Party Service Privacy Policies</h2>
          <p>For more information about how our third-party service providers handle your data, please refer to their privacy policies:</p>
          <ul>
            <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Play Services Privacy Policy</a></li>
            <li><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Google Analytics for Firebase Privacy Policy</a></li>
            <li><a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer">Google Analytics Privacy Policy</a></li>
            <li><a href="https://developer.android.com/health-and-fitness/guides/health-connect" target="_blank" rel="noopener noreferrer">Health Connect Privacy Information</a></li>
          </ul>

          <h1>California Privacy Rights (CCPA)</h1>
          <p>If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):</p>
          <ul>
            <li>Right to know what personal information is collected, used, shared, or sold</li>
            <li>Right to delete personal information</li>
            <li>Right to opt-out of the sale of personal information (Note: We do not sell personal information)</li>
            <li>Right to non-discrimination for exercising your privacy rights</li>
          </ul>

          <h1>European Privacy Rights (GDPR)</h1>
          <p>If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):</p>
          <ul>
            <li>Right to access, correct, or delete your personal data</li>
            <li>Right to restrict or object to data processing</li>
            <li>Right to data portability</li>
            <li>Right to withdraw consent at any time</li>
            <li>Right to lodge a complaint with a supervisory authority</li>
          </ul>

          <h1>Contact Us</h1>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>

          <ul>
            <li><strong>Email:</strong> office@shalibowellness.com</li>
            <li><strong>Company:</strong> Fitmate inc.</li>
            <li><strong>Address:</strong> Beit Zait 32, Israel</li>
          </ul>

          <p>We will respond to your inquiry within 30 days of receipt.</p>

          <h1>Consent</h1>
          <p>By using our Service, you consent to our Privacy Policy and agree to its terms. If you do not agree with this Privacy Policy, please do not use our Service.</p>

        </Paper>
        <Box mt={5}>
          <MadeWithLove />
        </Box>
      </Container>
    </div>
  );
};

export default withApollo(PrivacyPolicy);